import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Layout, List, Row, Skeleton } from 'antd';
import useAgents from '../hooks/useAgents';
import useConversations from '../hooks/useConversations';
import ConversationListItem from './ConversationListItem';
import ConversationsHeader from './ConversationsHeader';
import AdvancedFilters from './AdvancedFilters/AdvancedFilters';
import { format } from 'date-fns';
import { debounce } from 'lodash';
import { useTheme } from '../../Shared/GlobalContext/ThemeContext';

const { Content } = Layout;

// Custom Conversation Skeleton component
const ConversationSkeleton = () => {
    const { isDarkMode } = useTheme();
    
    const themeColors = {
        background: isDarkMode ? '#1f1f1f' : '#fff',
        border: isDarkMode ? '#303030' : '#f0f0f0',
        skeletonColor: isDarkMode ? 'rgba(255, 255, 255, 0.08)' : '#f5f5f5'
    };
    
    return (
        <div style={{ 
            padding: '8px 16px', 
            borderBottom: `1px solid ${themeColors.border}`,
            display: 'flex',
            alignItems: 'flex-start',
            background: themeColors.background,
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflow: 'hidden'
        }}>
            {/* Avatar with status badge */}
            <div style={{ position: 'relative', marginRight: '12px', flexShrink: 0 }}>
                <div style={{ 
                    width: '28px', 
                    height: '28px', 
                    borderRadius: '50%', 
                    backgroundColor: themeColors.skeletonColor 
                }}></div>
                <div style={{ 
                    position: 'absolute', 
                    right: '0', 
                    bottom: '0',
                    width: '6px', 
                    height: '6px', 
                    borderRadius: '50%', 
                    backgroundColor: themeColors.skeletonColor 
                }}></div>
            </div>
            
            {/* Content */}
            <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 0, overflow: 'hidden' }}>
                {/* Title and time row */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Skeleton.Input 
                        active 
                        style={{ width: '80px', height: '14px' }} 
                        size="small"
                    />
                    <Skeleton.Input 
                        active 
                        style={{ width: '20px', height: '12px' }} 
                        size="small"
                    />
                </div>
                
                {/* Message preview */}
                <Skeleton.Input 
                    active 
                    style={{ width: '140px', height: '14px' }} 
                    size="small"
                />
            </div>
        </div>
    );
};

const ConversationsList = ({ onSelectConversation, selectedId, conversations, setConversations }) => {
    const { isDarkMode } = useTheme();
    const [hoveredIndex, setHoveredIndex] = useState(null);
    const [openAdvancedFilters, setOpenAdvancedFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true);
    const [loading, setLoading] = useState(true);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [searchText, setSearchText] = useState('');
    const [filters, setFilters] = useState({
        status: null,
        dateRange: null,
        tags: null
    });
    const perPage = 25;

    const {
        agents,
        selectedAgent,
        loadingAgents,
        setSelectedAgent,
        fetchAgents
    } = useAgents();

    const { fetchConversations } = useConversations();

    const sortedConversations = useMemo(() => {
        if (!conversations || !Array.isArray(conversations)) return [];
        
        return [...conversations].sort((a, b) => {
            // Make sure a and b are valid objects
            if (!a || !b) return 0;
            
            const aTime = a.updated_time;
            const bTime = b.updated_time;
            
            return new Date(bTime) - new Date(aTime); // descending order
        }).filter(conversation => conversation); // Filter out any undefined/null items
    }, [conversations]);

    useEffect(() => {
        if (selectedAgent) {
            setCurrentPage(1);
            setSearchText('');
            fetchConversations({
                page: 1,
                perPage,
                selectedAgent,
                setConversations,
                setHasNextPage,
                setLoading,
                shouldAppend: false,
                filters: {},
                search_string: '',
            });
        }
    }, [selectedAgent, fetchConversations, setConversations, setHasNextPage, setLoading, perPage]);

    useEffect(() => {
        if (!selectedId && sortedConversations.length > 0) {
            onSelectConversation(sortedConversations[0]);
        }
    }, [selectedId, sortedConversations, onSelectConversation]);

    const handleScroll = (e) => {
        const { scrollTop, clientHeight, scrollHeight } = e.currentTarget;
        const threshold = 50;
        const isNearBottom = scrollHeight - scrollTop <= clientHeight + threshold;

        if (isNearBottom && !loading && hasNextPage) {
            const nextPage = currentPage + 1;
            setCurrentPage(nextPage);
            const formattedFilters = {
                ...filters,
                created_at_start: filters.dateRange ?
                    format(filters.dateRange?.[0].$d, 'yyyy-MM-dd hh:mm:ss') : null,
                created_at_end: filters.dateRange ?
                    format(filters.dateRange?.[1].$d, 'yyyy-MM-dd hh:mm:ss') : null,
                dateRange: null,
            };
            fetchConversations({
                page: nextPage,
                perPage,
                selectedAgent,
                setConversations,
                setHasNextPage,
                setLoading,
                shouldAppend: true,
                search_string: searchText,
                filters: formattedFilters,
            });
        }
    };

    const handleApplyFilters = (newFilters) => {
        const formattedFilters = {
            ...newFilters,
            created_at_start: newFilters.dateRange ?
                format(newFilters.dateRange?.[0].$d, 'yyyy-MM-dd hh:mm:ss') : null,
            created_at_end: newFilters.dateRange ?
                format(newFilters.dateRange?.[1].$d, 'yyyy-MM-dd hh:mm:ss') : null,
            dateRange: null,
        };
        setFilters(newFilters);
        setCurrentPage(1);
        fetchConversations({
            page: 1,
            perPage,
            selectedAgent,
            filters: formattedFilters,
            setConversations,
            setHasNextPage,
            setLoading,
            shouldAppend: false,
            search_string: searchText,
        });
    };

    const handleResetFilters = () => {
        setFilters({
            status: null,
            dateRange: null,
            tags: null
        });
        setCurrentPage(1);
        fetchConversations({
            page: 1,
            perPage,
            selectedAgent,
            filters: {},
            setConversations,
            setHasNextPage,
            setLoading,
            shouldAppend: false
        });
    };

    const debouncedSearch = useCallback(
        debounce((searchValue) => {
            setCurrentPage(1);
            fetchConversations({
                page: currentPage,
                perPage,
                selectedAgent,
                filters: {},
                setConversations,
                setHasNextPage,
                setLoading: setLoadingSearch,
                shouldAppend: false,
                search_string: searchValue,
            });
        }, 500),
        [fetchConversations, selectedAgent, setConversations, setHasNextPage, setLoadingSearch, currentPage, perPage],
    );
    const handleSearchChange = (e) => {
        const search = e.target.value;
        setSearchText(search);
        debouncedSearch(search);
    };

    return (
        <>
            {openAdvancedFilters && <AdvancedFilters
                handleCancel={() => setOpenAdvancedFilters(false)}
                onApply={handleApplyFilters}
                initialValues={filters}
            />}
            <Layout style={{
                width: '100%',
                height: '100%',
                borderInlineEnd: `1px solid ${isDarkMode ? '#303030' : '#e8e8e8'}`,
                borderTop: 'none',
                background: isDarkMode ? '#141414' : '#fff',
                overflow: 'hidden'
            }}>
                <ConversationsHeader
                    agents={agents}
                    loading={loadingSearch}
                    loadingAgents={loadingAgents}
                    selectedAgent={selectedAgent}
                    onAgentChange={setSelectedAgent}
                    onAgentSearch={fetchAgents}
                    onOpenFilters={() => setOpenAdvancedFilters(true)}
                    onResetFilters={handleResetFilters}
                    onSearch={handleSearchChange}
                />

                <Content
                    style={{
                        overflow: 'auto',
                        background: isDarkMode ? '#1f1f1f' : '#fff',
                        padding: '8px 0',
                    }}
                    onScroll={handleScroll}
                >
                    {loading && sortedConversations.length === 0 ? (
                        <>
                            {[...Array(8)].map((_, index) => (
                                <ConversationSkeleton key={index} />
                            ))}
                        </>
                    ) : (
                        <List
                            loading={false}
                            dataSource={sortedConversations}
                            renderItem={(conversation, index) => (
                                conversation ? (
                                    <ConversationListItem
                                        key={conversation.id || index}
                                        conversation={conversation}
                                        selectedId={selectedId}
                                        onSelectConversation={onSelectConversation}
                                        index={index}
                                        hoveredIndex={hoveredIndex}
                                        setHoveredIndex={setHoveredIndex}
                                    />
                                ) : null
                            )}
                        />
                    )}

                    {loading && sortedConversations.length > 0 && hasNextPage && (
                        <Row justify="center" style={{ padding: '8px 0' }}>
                            <ConversationSkeleton />
                            <ConversationSkeleton />
                        </Row>
                    )}
                </Content>
            </Layout>
        </>
    );
};

export default ConversationsList;
